import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import axios, { AxiosError } from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { ChangeEvent } from "react";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import DepartamentoTable, {
  departamentoTableColumns,
} from "components/tables/DepartamentoTable";
import {
  departamentosBreadcrumbItems,
  DepartamentoTableType,
} from "data/departamentos";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";
import { usePermissionModal } from "contexts/PermissionModalContext";

const fetchDepartamentos = async () => {
  const { data } = await axios.get("/api/departamento");
  return data.data;
};

const Departamentos = () => {
  const { getQueryRetryHandler } = usePermissionModal();

  const dataQuery = useQuery<DepartamentoTableType[]>({
    queryKey: ["departamentos"],
    queryFn: fetchDepartamentos,
    placeholderData: keepPreviousData,
    retry: getQueryRetryHandler({
      queryKey: ["departamentos"],
      defaultMessage: "Você não possui permissão para consultar departamentos."
    }),
  });

  const table = useReactTable({
    data: dataQuery.data ?? [],
    columns: departamentoTableColumns,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  if (dataQuery.isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  if (dataQuery.error) {
    const error = dataQuery.error as AxiosError;
    if (error.response?.status === 403) {
      return (
        <div>
          <PageBreadcrumb items={departamentosBreadcrumbItems} />
          <div className="mb-9">
            <h2 className="mb-5">Departamentos</h2>

            <div className="mb-4">
              <div className="d-flex flex-wrap gap-3">
                <SearchBox
                  placeholder="Pesquisar departamentos"
                  onChange={() => { }}
                  disabled={true}
                />
                <div className="ms-xxl-auto">
                  <Button
                    variant="primary"
                    as={Link}
                    to="/cadastros/departamentos/novo"
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Cadastrar
                  </Button>
                </div>
              </div>
            </div>

            <AccessDeniedOverlay message="Você não tem permissão para consultar ou visualizar departamentos.">
              <div
                className="position-relative"
                style={{
                  filter: "blur(8px)",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              >
                <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Descrição</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array(5)
                          .fill(0)
                          .map((_, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>Departamento Exemplo</td>
                              <td>-</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </AccessDeniedOverlay>
          </div>
        </div>
      );
    }
    return (
      <div className="text-center py-5 text-danger">
        {(dataQuery.error as Error).message}
      </div>
    );
  }

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <div>
      <PageBreadcrumb items={departamentosBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-5">Departamentos</h2>

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Pesquisar departamentos"
                onChange={handleSearchInputChange}
              />
              <div className="ms-xxl-auto">
                <Button
                  variant="primary"
                  as={Link}
                  to="/cadastros/departamentos/novo"
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Cadastrar
                </Button>
              </div>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            <DepartamentoTable />
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Departamentos;
